/**
 * PATH: packages/bills/src/components/billsListGrouping.ts
 * FILE_ID: BILLS-PKG-CMP-LIST-GROUPING-001
 * PURPOSE: Pure I→O helpers that re-shape a flat bill list into a
 *          render-item stream with statement-children grouped under
 *          synthetic headers.
 *
 * WHY: The grouping logic for "explode statements into N children"
 *      (Phase 2 of the statement-explosion work — see
 *      organisation/services/bill_extractor/_explode.py for the
 *      backend) is non-trivial and easy to break. Lifting it out of
 *      BillsListPanel.tsx keeps the panel focused on rendering and
 *      lets us cover this logic with table-driven tests later
 *      without spinning up a React tree.
 *
 * FLOW:
 *   ┌─────────────┐   ┌─────────────────┐   ┌──────────────────┐
 *   │ BillListRow │ → │ groupSiblings() │ → │ RenderItem[]     │
 *   │ []          │   │ (this module)   │   │ for the panel    │
 *   └─────────────┘   └─────────────────┘   └──────────────────┘
 *
 * DEPENDENCIES: types.ts (BillListRow). Pure module — no React, no DOM.
 *
 * RELATED:
 *   - BillsListPanel.tsx           (consumer)
 *   - report/services/.../_explode.py (backend producer)
 */

import type { BillListRow } from '../types';

/**
 * Render variants for a single bill row.
 *
 * - ``standalone``: top-level row — no statement parent. Default.
 * - ``grouped-child``: one of ≥ 2 visible children of the same
 *   statement parent. Indented + has a tinted left bracket so the
 *   eye picks up the grouping. Provenance lives in the sibling
 *   ``GroupHeader`` directly above.
 * - ``orphan-child``: a child whose siblings were filtered out
 *   (e.g. a search hit only one of N). No header is rendered for
 *   groups of one — instead the row gets a small "from stmt" pill
 *   so provenance is preserved.
 */
export type BillRowVariant = 'standalone' | 'grouped-child' | 'orphan-child';

/**
 * One item in the rendered list: a date-bucket section header, a
 * statement-group header, or a single bill row tagged with its
 * display variant.
 */
export type RenderItem =
  | {
      kind: 'date-header';
      /** Stable React key — one per emitted section, e.g. ``date-today``. */
      key: string;
      /** Human label for the bucket — "Today" / "Yesterday" / …. */
      label: string;
    }
  | {
      kind: 'header';
      /** Stable React key — tied to the parent_bill_id so the same
       *  group keeps the same DOM node across re-renders. */
      key: string;
      vendorName: string;
      /** Number of visible children in the group (after filtering),
       *  not the total ``child_bill_count`` from the backend. */
      count: number;
      /** Sum of the visible siblings' ``total`` fields. */
      total: number;
      currency: string;
    }
  | { kind: 'row'; row: BillListRow; variant: BillRowVariant };

/**
 * Re-shape a flat list of bill rows into a stream of render items
 * that visually groups children of the same statement together.
 *
 *   in : [tax_invoice A, child of S1, child of S1, child of S1, tax_invoice B]
 *   out: [row(A,std), header(S1, 3), row(c1,grp), row(c2,grp), row(c3,grp), row(B,std)]
 *
 * Edge cases handled:
 *   - A child whose siblings were all filtered out becomes an
 *     ``orphan-child`` row — no header, but the row gets a
 *     "from stmt" pill so provenance is preserved.
 *   - Children of the same parent that appear non-contiguously in
 *     the input (e.g. backend ordered by ``extracted_at`` desc and
 *     the same statement was extracted in two batches) are pulled
 *     together under one header. We re-order the visible list so
 *     siblings stay adjacent. The first-occurrence index implicitly
 *     determines where the group lives in the output, keeping the
 *     overall order stable for the user.
 *   - Envelope rows (``is_envelope === true``) are skipped — the
 *     default API list filter hides them, but if the caller ever
 *     passes ``?include_envelopes=true`` we still don't want them
 *     mixed in with ordinary rows.
 *   - Rows with ``parent_bill_id`` set but no other visible siblings
 *     in the input (lone children) become ``orphan-child`` rows.
 *
 * pure: BillListRow[] → RenderItem[]
 */
export function groupSiblings(rows: BillListRow[]): RenderItem[] {
  const items: RenderItem[] = [];
  // Bucket children by parent_bill_id. Preserves input order within
  // each bucket (which matches the backend default — extracted_at
  // desc — and the order children were exploded out of the
  // statement).
  const childrenByParent = new Map<string, BillListRow[]>();
  for (const r of rows) {
    if (r.is_envelope) continue;
    if (!r.parent_bill_id) continue;
    const arr = childrenByParent.get(r.parent_bill_id) ?? [];
    arr.push(r);
    childrenByParent.set(r.parent_bill_id, arr);
  }

  const consumed = new Set<string>();
  for (const row of rows) {
    if (row.is_envelope) continue;
    if (consumed.has(row.id)) continue;

    if (!row.parent_bill_id) {
      items.push({ kind: 'row', row, variant: 'standalone' });
      consumed.add(row.id);
      continue;
    }

    const siblings = childrenByParent.get(row.parent_bill_id) ?? [row];
    if (siblings.length <= 1) {
      // Lone visible child — render with the "from stmt" pill, but
      // no header (a header for a single row would be more visual
      // noise than help).
      items.push({ kind: 'row', row, variant: 'orphan-child' });
      consumed.add(row.id);
      continue;
    }

    items.push({
      kind: 'header',
      key: `hdr-${row.parent_bill_id}`,
      vendorName: row.vendor_name || siblings[0]?.vendor_name || '',
      count: siblings.length,
      total: sumNumeric(siblings.map((s) => s.total)),
      currency: row.currency || siblings[0]?.currency || 'AUD',
    });
    for (const sib of siblings) {
      items.push({ kind: 'row', row: sib, variant: 'grouped-child' });
      consumed.add(sib.id);
    }
  }
  return items;
}

/** Received-date buckets the list is sectioned into, newest first. */
export type DateBucket = 'today' | 'yesterday' | 'week' | 'earlier';

const BUCKET_LABEL: Record<DateBucket, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  week: 'This week',
  earlier: 'Earlier',
};

/**
 * Classify a bill's arrival date into a display bucket, relative to
 * ``now`` (injectable for tests). Prefers ``email_received_at`` (what
 * the bookkeeper thinks of as "when it came in"), falling back to
 * ``created_at`` for manual bills. Future dates clamp to "today".
 *
 * pure: (BillListRow, Date) → DateBucket
 */
export function dateBucketOf(row: BillListRow, now: Date): DateBucket {
  const iso = row.email_received_at || row.created_at;
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return 'earlier';
  // Compare CALENDAR days (local midnight to local midnight), not raw
  // 24-hour windows — a bill received yesterday afternoon is "Yesterday"
  // even if fewer than 24 hours have elapsed.
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const startOfDay = new Date(d);
  startOfDay.setHours(0, 0, 0, 0);
  const days = Math.round(
    (startOfToday.getTime() - startOfDay.getTime()) / 86_400_000,
  );
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return 'week';
  return 'earlier';
}

/**
 * Second pure pass over the grouped stream: insert ``date-header``
 * items whenever the arrival-date bucket changes between consecutive
 * rows, so the panel renders "Today / Yesterday / This week / Earlier"
 * section labels.
 *
 * Rules:
 *   - A statement group is NEVER split across buckets — the group's
 *     bucket is its first child's, and the date header (if any) goes
 *     above the group header.
 *   - Headers only appear when the bucket CHANGES, so an interleaved
 *     (non-monotonic) input can re-emit a label; with the backend's
 *     default extracted_at-desc ordering this doesn't happen in
 *     practice, and re-emitting is more truthful than mis-bucketing.
 *   - Keys are suffixed with the emission index so React keys stay
 *     unique even if a label repeats.
 *
 * pure: (RenderItem[], Date) → RenderItem[]
 */
export function withDateSections(
  items: RenderItem[],
  now: Date = new Date(),
): RenderItem[] {
  const out: RenderItem[] = [];
  let lastBucket: DateBucket | null = null;
  let emitted = 0;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    // Resolve the row that determines this item's bucket: the item
    // itself for standalone/orphan rows, the first following row for
    // statement headers. Grouped children inherit the bucket their
    // header established (bucketRow stays null) so a group is never
    // split across date sections.
    let bucketRow: BillListRow | null = null;
    if (item.kind === 'row' && item.variant !== 'grouped-child') {
      bucketRow = item.row;
    } else if (item.kind === 'header') {
      const next = items[i + 1];
      bucketRow = next && next.kind === 'row' ? next.row : null;
    }
    if (bucketRow) {
      const bucket = dateBucketOf(bucketRow, now);
      if (bucket !== lastBucket) {
        out.push({
          kind: 'date-header',
          key: `date-${bucket}-${emitted++}`,
          label: BUCKET_LABEL[bucket],
        });
        lastBucket = bucket;
      }
    }
    out.push(item);
  }
  return out;
}

/**
 * Sum string/number "money" fields from the API into a JS number.
 * Treats null / unparseable entries as zero so we never crash on a
 * partial sibling whose extraction is still pending.
 *
 * pure: (string | number | null | undefined)[] → number
 */
export function sumNumeric(
  values: Array<string | number | null | undefined>,
): number {
  let acc = 0;
  for (const v of values) {
    if (v == null || v === '') continue;
    const n = typeof v === 'string' ? Number(v) : v;
    if (Number.isFinite(n)) acc += n;
  }
  return acc;
}
